import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Send, Remove, Update } from '../../services/service.api';
import { toast } from 'react-toastify';
import { Plus, Trash2, X, Megaphone } from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';
import FullScreenLoader from '../../components/FullScreenLoader';

function formatFCFA(n) { return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`; }

function CampaignModal({ campaign, onClose, onSave, t }) {
  const [form, setForm] = useState({
    libelle: campaign?.libelle || '',
    description: campaign?.description || '',
    date_debut: campaign?.date_debut ? campaign.date_debut.split('T')[0] : '',
    date_fin: campaign?.date_fin ? campaign.date_fin.split('T')[0] : '',
    budget_initial: campaign?.budget_initial || '',
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.libelle || !form.description || !form.budget_initial) {
      return toast.error('Libellé, description et budget sont obligatoires');
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{campaign ? 'Modifier la campagne' : t.campaigns.add}</h2>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t.campaigns.libelle} <span className="required">*</span></label>
              <input className="form-control" value={form.libelle}
                onChange={e => setForm({ ...form, libelle: e.target.value })} required
                placeholder="Ex: Campagne Hivernage 2026" />
            </div>
            <div className="form-group">
              <label className="form-label">{t.campaigns.description} <span className="required">*</span></label>
              <textarea className="form-control" value={form.description} rows={3} required
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Décrivez les objectifs de la campagne..." />
            </div>
            <div className="form-group">
              <label className="form-label">{t.campaigns.budgetInitial} <span className="required">*</span></label>
              <input type="number" min="0" className="form-control" value={form.budget_initial} required
                onChange={e => setForm({ ...form, budget_initial: e.target.value })}
                placeholder="0" />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.campaigns.dateDebut}</label>
                <input type="date" className="form-control" value={form.date_debut}
                  onChange={e => setForm({ ...form, date_debut: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t.campaigns.dateFin}</label>
                <input type="date" className="form-control" value={form.date_fin}
                  onChange={e => setForm({ ...form, date_fin: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const isInvestor = user?.role === 'investisseur';
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 30;

  const abortControllerRef = useRef(null);

  const fetchCampaigns = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const companyId = activeCompany?.id || '';
      const headers = { authorization: "Bearer " + token, 'Content-Type': 'application/json' };
      if (companyId) headers['X-Company-Id'] = companyId;

      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      params.append('_t', Date.now());
      const qs = params.toString() ? `?${params.toString()}` : '';

      const API_URL = process.env.REACT_APP_API_URL || "https://savaninvests-backend.onrender.com";
      const response = await fetch(`${API_URL}/campaigns${qs}`, { headers, signal });
      const res = await response.json();

      if (!signal.aborted && Array.isArray(res)) {
        setCampaigns(res);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // silently fail
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [token, activeCompany?.id]);

  useEffect(() => {
    setCampaigns([]); // Clear to prevent ghost data
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    const handleSwitched = () => fetchCampaigns();
    window.addEventListener('company-switched', handleSwitched);
    return () => window.removeEventListener('company-switched', handleSwitched);
  }, [fetchCampaigns]);

  const handleSave = async (form) => {
    try {
      let res;
      if (editCampaign) {
        res = await Update(`/campaigns/${editCampaign.id}`, form, token);
      } else {
        res = await Send('/campaigns', form, true, token);
      }
      if (res?.error) { toast.error(res.error); return; }
      toast.success(editCampaign ? 'Campagne modifiée' : 'Campagne créée ✓');
      setShowModal(false); setEditCampaign(null); fetchCampaigns();
    } catch { toast.error('Erreur'); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await Remove(`/campaigns/${deleteId}`, token);
      toast.success('Campagne supprimée');
      setDeleteId(null);
      fetchCampaigns();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="page-content page-enter">
      {loading && <FullScreenLoader message="Chargement des campagnes..." />}
      <div className="page-header">
        <div>
          <h1>{t.campaigns.title}</h1>
          <p>{campaigns.length} campagne(s)</p>
        </div>
        {isInvestor && (
          <button className="btn btn-primary" onClick={() => { setEditCampaign(null); setShowModal(true); }}>
            <Plus size={16} /> {t.campaigns.add}
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid-2">{[...Array(2)].map((_, i) => <div key={i} className="skeleton" style={{ height: 180 }} />)}</div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Megaphone size={48} opacity={0.3} /></div>
          <h3>{t.campaigns.noCampaigns}</h3>
          {isInvestor && <p>Créez votre première campagne d'investissement</p>}
        </div>
      ) : (
        <>
          <div className="grid-2">
            {campaigns.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(c => {
            const progression = c.budget_initial > 0
              ? Math.min(100, ((c.total_investi || 0) / c.budget_initial) * 100).toFixed(0)
              : 0;
            return (
              <div key={c.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
                      {c.libelle}
                    </div>
                    <span className={`badge ${c.statut === 'active' ? 'badge-success' : 'badge-muted'}`}>
                      {c.statut === 'active' ? t.campaigns.active : t.campaigns.closed}
                    </span>
                  </div>
                  {isInvestor && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditCampaign(c); setShowModal(true); }}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(c.id)}><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0 }}>{c.description}</p>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.8rem' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Progression</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{progression}%</span>
                  </div>
                  <div style={{ background: 'var(--border)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${progression}%`, height: '100%', background: 'linear-gradient(90deg, var(--primary), var(--primary-dark))', borderRadius: 4 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <div>
                    <div style={{ color: 'var(--text-muted)' }}>Budget</div>
                    <div className="number-format" style={{ fontWeight: 700 }}>{formatFCFA(c.budget_initial)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--text-muted)' }}>Investi</div>
                    <div className="number-format" style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatFCFA(c.total_investi)}</div>
                  </div>
                </div>
                {(c.date_debut || c.date_fin) && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    📅 {c.date_debut ? new Date(c.date_debut).toLocaleDateString('fr-FR') : '?'}
                    {c.date_fin ? ` → ${new Date(c.date_fin).toLocaleDateString('fr-FR')}` : ''}
                  </div>
                )}
              </div>
            );
          })}
          </div>
          {Math.ceil(campaigns.length / PAGE_SIZE) > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button className="btn btn-outline btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>← Préc.</button>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Page {currentPage} / {Math.ceil(campaigns.length / PAGE_SIZE)}</span>
              <button className="btn btn-outline btn-sm" disabled={currentPage === Math.ceil(campaigns.length / PAGE_SIZE)} onClick={() => setCurrentPage(p => p + 1)}>Suiv. →</button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <CampaignModal campaign={editCampaign} onClose={() => { setShowModal(false); setEditCampaign(null); }} onSave={handleSave} t={t} />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Supprimer cette campagne"
        message="Êtes-vous sûr de vouloir supprimer cette campagne d'investissement ? Attention, il est obligatoire de supprimer toutes les données (ventes, achats, investissements) qui y sont reliées. Cette action est irréversible."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
